<script setup lang="ts">
import { useStore } from '@/stores/store'
import { computed, readonly } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const store = useStore()
const {
  currentStep,
  shape,
  cushion,
  edgeSewing,
  selectedFabric,
  cushionBottom,
  diameter_a_circle,
  isButtons,
  width_a_rectangle,
  length_b_rectangle,
  isStitches,
  isButtonMiddle,
  buttonsCols,
  buttonsRows,
  stitchesCols,
  stitchesRows,
  buttonsDistribution,
  buttonsDistanceToEdge,
  stitchesDistribution,
  stitchesDistanceToEdge,
  topStitchesType,
  crossTopSewing,
} = storeToRefs(store)
const steps = readonly([
  { name: 'Form', step: 1 },
  { name: 'Materials', step: 2 },
  { name: 'Size & Buttons', step: 3 },
  { name: 'Overview', step: 4 },
])

const lastStep = computed(() => steps[steps.length - 1].step === currentStep.value)
const isInvalid = computed(() => {
  if (
    store.isCircle &&
    diameter_a_circle.value &&
    Number(diameter_a_circle.value) >= 30 &&
    Number(diameter_a_circle.value) <= 200 &&
    isButtons.value &&
    isStitches.value
  ) {
    if (isButtons.value == 'buttons_no' && isStitches.value == 'stitches_no') {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isButtonMiddle.value &&
      isStitches.value == 'stitches_no'
    ) {
      return false
    }
    if (
      isButtons.value == 'buttons_no' &&
      isStitches.value == 'stitches_yes' &&
      crossTopSewing.value &&
      topStitchesType.value
    ) {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isStitches.value == 'stitches_yes' &&
      crossTopSewing.value &&
      topStitchesType.value
    ) {
      return false
    }
  }
  if (
    !store.isCircle &&
    isButtons.value &&
    isStitches.value &&
    width_a_rectangle.value &&
    length_b_rectangle.value &&
    Number(width_a_rectangle.value) >= 30 &&
    Number(width_a_rectangle.value) <= 240 &&
    Number(length_b_rectangle.value) >= 30 &&
    Number(length_b_rectangle.value) <= 240
  ) {
    if (isButtons.value == 'buttons_no' && isStitches.value == 'stitches_no') {
      return false
    }
    if (
      isButtons.value == 'buttons_yes' &&
      buttonsDistribution.value &&
      isStitches.value == 'stitches_no' &&
      buttonsCols.value &&
      buttonsRows.value
    ) {
      if (
        buttonsDistribution.value == 'distribution_fixed_distance' &&
        buttonsDistanceToEdge.value
      ) {
        return false
      }
      if (buttonsDistribution.value == 'distribution_simple_split') {
        return false
      }
    }
    if (
      isButtons.value == 'buttons_no' &&
      isStitches.value == 'stitches_yes' &&
      stitchesCols.value &&
      stitchesRows.value &&
      topStitchesType.value
    ) {
      if (
        stitchesDistribution.value == 'stitches_distribution_fixed_distance' &&
        stitchesDistanceToEdge.value
      ) {
        return false
      }
      if (stitchesDistribution.value == 'stitches_distribution_simple_split') {
        return false
      }
    }
    if (
      isButtons.value == 'buttons_yes' &&
      isStitches.value == 'stitches_yes' &&
      buttonsCols.value &&
      buttonsRows.value &&
      topStitchesType.value
    ) {
      if (
        buttonsDistribution.value == 'distribution_fixed_distance' &&
        buttonsDistanceToEdge.value
      ) {
        return false
      }
      if (buttonsDistribution.value == 'distribution_simple_split') {
        return false
      }
    }
  }
  return true
})
const changeStep = (index: number) => {
  if (index == 1 && !(shape.value && cushion.value && edgeSewing.value)) {
    return
  }
  if (index == 2 && !(cushionBottom.value && selectedFabric.value.name)) {
    return
  }
  if (index == 3 && isInvalid.value) {
    return
  }

  currentStep.value = steps[index].step
  document.querySelector('.configurator_right_column')?.scrollIntoView({ behavior: 'smooth' })
}
const subHeader = 'Design your own cushion'
</script>

<template>
  <div>
    <h2 class="sub-header font-montserrat">
      {{ t(subHeader) }}
    </h2>
    <div class="navigation-wrapper" :class="{ 'tip-active': lastStep }" v-if="currentStep > 0">
      <ul class="navigation-steps">
        <li
          v-for="(step, index) in steps"
          :key="step.step"
          :class="{ active: currentStep >= step.step }"
          class="nav-step w-1/4 lg:w-auto"
        >
          <button
            type="button"
            class="flex flex-col w-full lg:w-max lg:min-w-[120px]"
            @click="changeStep(index)"
          >
            <div class="nav-step-progress" :class="{ active: currentStep >= step.step }"></div>
            <div class="nav-step-title-wrapper">
              <svg
                v-show="step.step < currentStep"
                class=""
                width="14"
                height="11"
                viewBox="0 0 14 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5.49999L5 9.83332L13 1.16666"
                  stroke="black"
                  stroke-opacity="0.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="nav-step-title">{{ t(step.name) }}</span>
            </div>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.sub-header {
  font-size: 32px;
  font-weight: 500;
}
.navigation-wrapper {
  position: relative;
  letter-spacing: 1px;
  margin-top: 16px;
  margin-bottom: 24px;
}

.navigation-steps {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
}

.navigation-steps .nav-step {
  display: flex;
}

.navigation-steps .nav-step.active .nav-step-title {
  position: relative;
  text-overflow: ellipsis;
  overflow: hidden;
  text-wrap: nowrap;
}

.navigation-steps .nav-step .nav-step-progress {
  border-radius: 4px;
  background-color: hsla(0, 0%, 0%, 0.03);
  width: 100%;
  height: 8px;
  margin-bottom: 4px;
}

.navigation-steps .nav-step .nav-step-progress.active {
  background-color: hsla(0, 0%, 25%, 1);
  width: 100%;
}

.navigation-steps .nav-step .nav-step-title {
  position: relative;
}
.navigation-steps .nav-step .nav-step-title-wrapper {
  display: none;
}
.navigation-steps .nav-step.active .nav-step-title-wrapper {
  display: inline-flex;
  flex-wrap: nowrap;
  justify-content: start;
  align-items: center;
  font-size: 12px;
  gap: 4px;
}
@media (min-width: 1024px) {
  .navigation-wrapper {
    margin-top: 24px;
    margin-bottom: 40px;
  }
  .sub-header {
    font-size: 48px;
  }
  .navigation-steps .nav-step.active .nav-step-title-wrapper {
    font-size: 14px;
    gap: 8px;
  }

  .navigation-steps .nav-step .nav-step-progress {
    min-width: 120px;
  }
}
</style>
